'use strict';

import ActionTypes from '../constants/ActionTypes';
import AppDispatcher from '../AppDispatcher';
import UserAPI from '../api/UserAPI';
import UserStore from '../stores/UserStore';
import ActionUtils from '../utils/ActionUtils';
import PopupManager from '../utils/PopupManager';
import EmailFlags from '../constants/EmailFlags';
import invariant from 'react/lib/invariant';
import { values } from 'underscore';
import UserSubscribersStore from '../stores/UserSubscribersStore';
import UserSubscriptionsStore from '../stores/UserSubscriptionsStore';
import SearchUserStore from '../stores/SearchUserStore';
import AnalyticsSources from '../constants/AnalyticsSources';
import handleUnauthAction from '../utils/handleUnauthAction';
import config from 'config';

const UserActionCreators = {
  requestUser(userId, fields) {
    ActionUtils.requestEntity({
      idAttribute: 'userId',
      userId: userId,
      store: UserStore,
      action: ActionTypes.REQUEST_USER,
      fields: fields,
      callAPI: UserAPI.requestUser
    });
  },

  toggleServiceAccountHidden(serviceType, hidden) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.TOGGLE_SERVICE_ACCOUNT_HIDDEN,
      serviceType: serviceType
    });

    UserAPI.toggleServiceAccountHidden(serviceType, hidden);
  },

  connectServiceAccount(serviceType) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.CONNECT_SERVICE_ACCOUNT,
      serviceType: serviceType
    });

    PopupManager.openConnect(serviceType);
  },

  loginWithServiceAccount(serviceType) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.LOGIN_WITH_SERVICE_ACCOUNT,
      serviceType: serviceType
    });

    PopupManager.openLogin(serviceType, { popup: !config.isAuthPopup });
  },

  signupWithServiceAccount(serviceType) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.SIGNUP_WITH_SERVICE_ACCOUNT,
      serviceType: serviceType
    });

    PopupManager.openSignup(serviceType, { popup: !config.isAuthPopup });
  },

  disconnectServiceAccount(serviceType) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DISCONNECT_SERVICE_ACCOUNT,
      serviceType: serviceType
    });

    UserAPI.disconnectServiceAccount(serviceType);
  },

  saveSettings(settings) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.SAVE_SETTINGS
    });

    UserAPI.saveSettings(settings);
  },

  requestResetPassword(data) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.REQUEST_RESET_PASSWORD
    });

    UserAPI.requestResetPassword(data);
  },

  resetPassword(data) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.RESET_PASSWORD
    });

    UserAPI.resetPassword(data);
  },

  logOut() {
    AppDispatcher.handleViewAction({
      type: ActionTypes.LOGOUT
    });

    window.location = '/logout';
  },

  sendFeedback(data) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.FEEDBACK
    });

    UserAPI.sendFeedback(data);
  },

  resendUnconfirmedEmail() {
    AppDispatcher.handleViewAction({
      type: ActionTypes.RESEND_UNCONFIRMED_EMAIL
    });

    UserAPI.resendUnconfirmedEmail();
  },

  cancelUnconfirmedEmail() {
    AppDispatcher.handleViewAction({
      type: ActionTypes.CANCEL_UNCONFIRMED_EMAIL
    });

    UserAPI.cancelUnconfirmedEmail();
  },

  resubscribeEmail(data) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.RESUBSCRIBE_EMAIL
    });

    UserAPI.resubscribeEmail(data);
  },

  enableEmailNotification(flag) {
    invariant(values(EmailFlags).indexOf(flag) > -1, 'Expected one of EmailFlags, got %s', flag);
    AppDispatcher.handleViewAction({
      type: ActionTypes.ENABLE_EMAIL_NOTIFICATION,
      flag: flag
    });

    UserAPI.saveEmailNotificationSettings();
  },

  disableEmailNotification(flag) {
    invariant(values(EmailFlags).indexOf(flag) > -1, 'Expected one of EmailFlags, got %s', flag);

    AppDispatcher.handleViewAction({
      type: ActionTypes.DISABLE_EMAIL_NOTIFICATION,
      flag: flag
    });

    UserAPI.saveEmailNotificationSettings();
  },

  followUser(userId) {
    if (handleUnauthAction(AnalyticsSources.FOLLOW_USER)) {
      return;
    }

    AppDispatcher.handleViewAction({
      type: ActionTypes.FOLLOW_USER,
      userId: userId
    });

    UserAPI.followUser(userId);
  },

  unfollowUser(userId) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.UNFOLLOW_USER,
      userId: userId
    });

    UserAPI.unfollowUser(userId);
  },

  requestUserSubscriberPage(userId, isInitialRequest, allowInvalidate) {
    if (allowInvalidate && isInitialRequest) {
      // Keep everyone's subscribers fresh
      AppDispatcher.handleViewAction({
        type: ActionTypes.INVALIDATE_USER_SUBSCRIBERS,
        userId: userId
      });
    }

    ActionUtils.requestIndexedPage({
      userId: userId,
      idAttribute: 'userId',
      isInitialRequest: isInitialRequest,
      store: UserSubscribersStore,
      action: ActionTypes.REQUEST_USER_SUBSCRIBERS,
      callAPI: UserAPI.requestUserSubscriberPage
    });
  },

  requestUserSubscriptionPage(userId, isInitialRequest, allowInvalidate) {
    if (allowInvalidate && isInitialRequest && UserStore.isMe(userId)) {
      // Keep own subscriptions fresh
      AppDispatcher.handleViewAction({
        type: ActionTypes.INVALIDATE_USER_SUBSCRIPTIONS,
        userId: userId
      });
    }

    ActionUtils.requestIndexedPage({
      userId: userId,
      idAttribute: 'userId',
      isInitialRequest: isInitialRequest,
      store: UserSubscriptionsStore,
      action: ActionTypes.REQUEST_USER_SUBSCRIPTIONS,
      callAPI: UserAPI.requestUserSubscriptionPage
    });
  },

  requestSearchPage(terms, isInitialRequest) {
    ActionUtils.requestIndexedPage({
      terms: terms,
      idAttribute: 'terms',
      isInitialRequest: isInitialRequest,
      store: SearchUserStore,
      action: ActionTypes.REQUEST_USER_SEARCH,
      callAPI: UserAPI.requestSearchPage
    });
  }
};

export default UserActionCreators;


/** WEBPACK FOOTER **
 ** ./actions/UserActionCreators.js
 **/